import { LogsEvent, datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { config } from 'config'

interface ErrorFilter {
  name: string
  test: (message: string) => boolean
}

const ddApplicationID = process.env.REACT_APP_DD_RUM_APPLICATION_ID
const ddClientToken = process.env.REACT_APP_DD_RUM_CLIENT_TOKEN
const environment =
  process.env.REACT_APP_DD_RUM_ENVIRONMENT ||
  window?.location?.hostname ||
  process.env.NODE_ENV

const REACT_APP_API_URL = config.urls.api
const REACT_APP_ATLAS_URL = config.urls.atlas

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&') // $& means the whole matched string
}

export const logger = datadogLogs.createLogger('ddLogger', {
  handler: 'console',
})

const errorFilters: ErrorFilter[] = [
  {
    name: 'EmailLinkValidation',
    test: (message) =>
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(
        message,
      ),
  },
]

const initializeDatadogRUM = () => {
  if (environment && ddApplicationID && ddClientToken) {
    const ddConfig = {
      applicationId: ddApplicationID,
      clientToken: ddClientToken,
      datacenter: 'us',
      env: environment,
      service: 'joor-react',
      sampleRate: 100,
      replaySampleRate: 0,
      silentMultipleInit: true,
      trackSessionAcrossSubdomains: true,
      version: process.env.REACT_APP_VERSION,
    }
    const allowedTracingOrigins = []
    if (REACT_APP_ATLAS_URL !== undefined) {
      allowedTracingOrigins.push(new URL(REACT_APP_ATLAS_URL).origin)
    }
    if (REACT_APP_API_URL !== undefined) {
      allowedTracingOrigins.push(
        new RegExp(
          escapeRegExp('https://') +
            '.*' +
            escapeRegExp(
              REACT_APP_API_URL.slice(
                REACT_APP_API_URL.indexOf(environment) + environment.length,
              ),
            ),
        ),
      )
    }
    datadogRum.init({
      ...ddConfig,
      allowedTracingOrigins,
      // Override sampleRate only for datadogRUM
      sampleRate: 10,
      beforeSend: (event, context) => {
        event.context = {
          ...event.context,
          response:
            'response' in context && context.response
              ? {
                  headers: context.response.headers,
                  body: context.response.body,
                  status: context.response.status,
                }
              : undefined,
        }
      },
    })

    datadogLogs.init({
      ...ddConfig,
      forwardErrorsToLogs: true,
      // @ts-expect-error https://github.com/DataDog/browser-sdk/issues/1413
      forwardConsoleLogs: 'error',
      // Filter out error that might be caused by link validation/ pre-fetch rendering from email clicks
      // https://github.com/DataDog/browser-sdk/issues/2715
      beforeSend: (event: LogsEvent) => {
        if (event?.message && typeof event.message === 'string') {
          const msg = event.message
          for (const filter of errorFilters) {
            if (filter.test(msg)) {
              return false
            }
          }
        }
        return true
      },
    })
    if (process.env.NODE_ENV !== 'development') {
      logger.setHandler('http')
    }
  }
}

export const setLocationForDatadogRUM = (location: any) => {
  datadogRum.addRumGlobalContext('location', location)
}

export const setUserForDatadog = ({
  userId,
  accountId,
}: {
  userId: number | null
  accountId: number | null
}) => {
  if (environment && ddApplicationID && ddClientToken && userId && accountId) {
    // First-class user identity: populates the RUM "User" panel and lets
    // sessions be filtered by usr.id (the global context below does not).
    datadogRum.setUser({
      id: String(userId),
      accountId: accountId,
    })
    datadogRum.addRumGlobalContext('user', {
      userId: userId,
      accountId: accountId,
    })
    datadogLogs.addLoggerGlobalContext('user', {
      userId: userId,
      accountId: accountId,
    })
  }
}

export const reportRenderErrorToDatadog = (
  error: Error,
  info?: { componentStack?: string },
) => {
  datadogRum.addError(error, {
    source: 'error-boundary',
    componentStack: info?.componentStack,
  })
}

export const logMutationError = (
  mutationName: string,
  variables: Record<string, unknown>,
  errorMsg: string,
): void => {
  if (!environment || !ddApplicationID || !ddClientToken) {
    return
  }

  datadogLogs.addLoggerGlobalContext('mutation', {
    mutationName,
    variables,
  })
  console.error(errorMsg)
  datadogLogs.removeLoggerGlobalContext('mutation')
}

export default initializeDatadogRUM
